import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";

type ShopTippingState = {
  enabled: boolean;
  amount1?: number;
  amount2?: number;
  amount3?: number;
};

type TipAmountField = "amount1" | "amount2" | "amount3";

type TipAmountDrafts = Record<TipAmountField, string>;

function tipAmountToDraft(value: number | undefined): string {
  return value !== undefined ? String(value) : "";
}

function resolveTipAmountDrafts(tipping: ShopTippingState): TipAmountDrafts {
  return {
    amount1: tipAmountToDraft(tipping.amount1),
    amount2: tipAmountToDraft(tipping.amount2),
    amount3: tipAmountToDraft(tipping.amount3),
  };
}

function resolveShopProductIds(product: ProductEditPageProduct): string[] {
  return (product.meeting?.shop?.productIds ?? [])
    .map((id) => String(id).trim())
    .filter(Boolean);
}

function resolveShopTipping(product: ProductEditPageProduct): ShopTippingState {
  return {
    enabled: product.meeting?.shop?.tipping?.enabled ?? false,
    amount1: product.meeting?.shop?.tipping?.amount1,
    amount2: product.meeting?.shop?.tipping?.amount2,
    amount3: product.meeting?.shop?.tipping?.amount3,
  };
}

interface CatalogProductLite {
  _id: string;
  name?: string;
}

export function ProductEditShopTab({
  productId,
  product,
}: {
  productId: string;
  product: ProductEditPageProduct;
}) {
  const [isSavingShop, setIsSavingShop] = useState(false);
  const [shopProductIds, setShopProductIds] = useState(() =>
    resolveShopProductIds(product),
  );
  const [shopTipping, setShopTipping] = useState<ShopTippingState>(() =>
    resolveShopTipping(product),
  );
  const [tipAmountDrafts, setTipAmountDrafts] = useState<TipAmountDrafts>(() =>
    resolveTipAmountDrafts(resolveShopTipping(product)),
  );
  const [catalogProducts, setCatalogProducts] = useState<CatalogProductLite[]>([]);

  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then((data) => {
        const loaded = (data.products as CatalogProductLite[] | undefined) ?? [];
        setCatalogProducts(loaded);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setShopProductIds(resolveShopProductIds(product));
  }, [product.meeting?.shop?.productIds]);

  useEffect(() => {
    const tipping = resolveShopTipping(product);
    setShopTipping(tipping);
    setTipAmountDrafts(resolveTipAmountDrafts(tipping));
  }, [
    product.meeting?.shop?.tipping?.enabled,
    product.meeting?.shop?.tipping?.amount1,
    product.meeting?.shop?.tipping?.amount2,
    product.meeting?.shop?.tipping?.amount3,
  ]);

  const selectableShopProducts = useMemo(
    () => catalogProducts.filter((entry) => entry._id !== productId),
    [catalogProducts, productId],
  );

  const saveShopPatch = async ({
    patch,
    onError,
  }: {
    patch: Record<string, unknown>;
    onError?: () => void;
  }) => {
    setIsSavingShop(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meeting: { shop: patch } }),
      });
      const data = await response.json();
      if (!response.ok) {
        onError?.();
        alert(data.error || "Failed to update shop settings");
      }
    } catch (error) {
      console.error("Failed to update meeting shop:", error);
      onError?.();
      alert("An error occurred while saving");
    } finally {
      setIsSavingShop(false);
    }
  };

  const handleShopProductToggle = async ({
    selectedProductId,
    checked,
  }: {
    selectedProductId: string;
    checked: boolean;
  }) => {
    const previous = shopProductIds;
    const next = checked
      ? [...new Set([...previous, selectedProductId])]
      : previous.filter((id) => id !== selectedProductId);

    setShopProductIds(next);
    await saveShopPatch({
      patch: { productIds: next },
      onError: () => setShopProductIds(previous),
    });
  };

  const handleTippingChange = async ({
    next,
    previous,
  }: {
    next: ShopTippingState;
    previous: ShopTippingState;
  }) => {
    setShopTipping(next);
    await saveShopPatch({
      patch: { tipping: next },
      onError: () => setShopTipping(previous),
    });
  };

  const handleTipAmountDraftChange = ({
    field,
    value,
  }: {
    field: TipAmountField;
    value: string;
  }) => {
    setTipAmountDrafts((previous) => ({ ...previous, [field]: value }));
  };

  const commitTipAmount = async ({
    field,
    value,
  }: {
    field: TipAmountField;
    value: string;
  }) => {
    const previous = shopTipping;
    const next: ShopTippingState = { ...shopTipping };

    if (value === "" || value === ".") {
      delete next[field];
    } else {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setTipAmountDrafts((drafts) => ({
          ...drafts,
          [field]: tipAmountToDraft(previous[field]),
        }));
        return;
      }
      next[field] = numValue;
    }

    if (previous[field] === next[field]) {
      setTipAmountDrafts((drafts) => ({
        ...drafts,
        [field]: tipAmountToDraft(next[field]),
      }));
      return;
    }

    setShopTipping(next);
    setTipAmountDrafts((drafts) => ({
      ...drafts,
      [field]: tipAmountToDraft(next[field]),
    }));
    await saveShopPatch({
      patch: { tipping: next },
      onError: () => {
        setShopTipping(previous);
        setTipAmountDrafts((drafts) => ({
          ...drafts,
          [field]: tipAmountToDraft(previous[field]),
        }));
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Shop products</Label>
        <p className="text-sm text-muted-foreground">
          Products shown in the livestream viewer shop overlay.
        </p>
        {selectableShopProducts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No other products found — create products first.
          </p>
        ) : (
          <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-2">
            {selectableShopProducts.map((entry) => {
              const checked = shopProductIds.includes(entry._id);

              return (
                <label
                  key={entry._id}
                  className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                >
                  <Checkbox
                    checked={checked}
                    disabled={isSavingShop}
                    onCheckedChange={(value) => {
                      void handleShopProductToggle({
                        selectedProductId: entry._id,
                        checked: value === true,
                      });
                    }}
                  />
                  <span>{entry.name?.trim() || "Untitled product"}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-md border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label>Tipping</Label>
            <p className="text-sm text-muted-foreground">
              Let viewers send a tip from the livestream shop, even without shop
              products.
            </p>
          </div>
          <Switch
            checked={shopTipping.enabled}
            disabled={isSavingShop}
            onCheckedChange={(checked) => {
              void handleTippingChange({
                previous: shopTipping,
                next: { ...shopTipping, enabled: checked },
              });
            }}
          />
        </div>

        {shopTipping.enabled ? (
          <div className="space-y-4 border-l-2 border-muted pl-6">
            <div className="space-y-2">
              <Label htmlFor="shop-tip-amount1">Tip Amount 1 ($)</Label>
              <Input
                id="shop-tip-amount1"
                type="number"
                step="0.01"
                min="0"
                disabled={isSavingShop}
                value={tipAmountDrafts.amount1}
                onChange={(event) => {
                  handleTipAmountDraftChange({
                    field: "amount1",
                    value: event.target.value,
                  });
                }}
                onBlur={(event) => {
                  void commitTipAmount({ field: "amount1", value: event.target.value });
                }}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-tip-amount2">Tip Amount 2 ($)</Label>
              <Input
                id="shop-tip-amount2"
                type="number"
                step="0.01"
                min="0"
                disabled={isSavingShop}
                value={tipAmountDrafts.amount2}
                onChange={(event) => {
                  handleTipAmountDraftChange({
                    field: "amount2",
                    value: event.target.value,
                  });
                }}
                onBlur={(event) => {
                  void commitTipAmount({ field: "amount2", value: event.target.value });
                }}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-tip-amount3">Tip Amount 3 ($)</Label>
              <Input
                id="shop-tip-amount3"
                type="number"
                step="0.01"
                min="0"
                disabled={isSavingShop}
                value={tipAmountDrafts.amount3}
                onChange={(event) => {
                  handleTipAmountDraftChange({
                    field: "amount3",
                    value: event.target.value,
                  });
                }}
                onBlur={(event) => {
                  void commitTipAmount({ field: "amount3", value: event.target.value });
                }}
                placeholder="Enter amount"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
